/**
 * KpiCard — single KPI tile for the traffic dashboard.
 *
 * Renders label, value, and previous-period delta with an up/down
 * arrow rendered via Tailwind tokens (green for up, red for down,
 * neutral for flat). Empty-state: shows "0" as the value with a
 * muted "—" for the delta (no data yet on either side of the window).
 */
import React from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

function _formatInt(n) {
  if (n === null || n === undefined) return "0";
  return n.toLocaleString("en-US");
}

export default function KpiCard({ label, tile, testIdSuffix, active, onClick }) {
  const value = tile?.value ?? 0;
  const deltaPct = tile?.delta_pct;
  const direction = tile?.delta_direction || "flat";
  const isClickable = typeof onClick === "function";

  const deltaColor =
    direction === "up"
      ? "text-emerald-600"
      : direction === "down"
      ? "text-rose-600"
      : "text-neutral-500";

  const DeltaIcon =
    direction === "up"
      ? ArrowUpRight
      : direction === "down"
      ? ArrowDownRight
      : Minus;

  return (
    <Card
      data-testid={`analytics-kpi-${testIdSuffix}`}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      style={{
        background: "var(--pp-v3-surface)",
        borderColor: active ? "var(--pp-orange)" : "var(--pp-border)",
        cursor: isClickable ? "pointer" : "default",
        outline: active ? "2px solid var(--pp-orange)" : "none",
        outlineOffset: active ? "2px" : "0",
      }}
    >
      <CardContent className="p-5">
        <div
          className="text-[12px] font-bold uppercase tracking-[0.06em]"
          style={{ color: "var(--pp-text-soft)" }}
        >
          {label}
        </div>
        <div
          className="mt-2 text-3xl font-semibold tracking-tight"
          style={{ color: "var(--pp-text)" }}
          data-testid={`analytics-kpi-${testIdSuffix}-value`}
        >
          {_formatInt(value)}
        </div>
        <div
          className={`mt-2 inline-flex items-center gap-1 text-xs font-medium ${deltaColor}`}
          data-testid={`analytics-kpi-${testIdSuffix}-delta`}
          data-delta-direction={direction}
        >
          <DeltaIcon className="h-3.5 w-3.5" strokeWidth={2.5} />
          {deltaPct === null || deltaPct === undefined ? (
            <span>—</span>
          ) : (
            <span>{deltaPct > 0 ? `+${deltaPct}%` : `${deltaPct}%`}</span>
          )}
          <span
            className="ml-1 text-[11px]"
            style={{ color: "var(--pp-text-soft)" }}
          >
            vs prior period
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
